import { gql } from '@apollo/client';
import { debounce, remove } from 'lodash-es';
import type { ComponentType, FC, ReactElement } from 'react';
import { createContext, memo } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { getCurrentLocaleTimestamp, getCurrentTimezone } from '@orion/fe-utils';

import { AuditCreationEventsEnum } from 'common/log/auditevents';

import type { AuthState, AuthUser } from '../auth/types';
import { loggerClient } from '../graphql/client';

interface CIAuditLog {
    localTimestamp: Date | string;
    localTimezone: string;
    source: string;

    studyId?: string | null;
    siteId?: string | null;
    subjectId?: string | null;
    relatedEntityId?: string | null;
    relatedEntityName?: string | null;
    applicationSource: string;
    action?: string;
    message: string;
    data: object | null;
}

const sendLogGQL = gql`
    mutation SendLog($log: InputCreateApplicationAuditData!) {
        createApplicationAuditData(input: $log) {
            pk
        }
    }
`;

export interface CIAuditLogMeta {
    relatedEntityName?: string | null;
    studyId?: string | null;
    siteId?: string | null;
    subjectId?: string | null;
    relatedEntityId?: string | null;
    // Callers attach extra, entity-specific fields that are only forwarded to the audit data
    [key: string]: unknown;
}

function convertLogToAPI(
    action: string | undefined,
    message: string,
    data: object | null,
    meta: CIAuditLogMeta | undefined
): CIAuditLog {
    return {
        localTimestamp: getCurrentLocaleTimestamp(),
        localTimezone: getCurrentTimezone(),
        source: '0.0.0.0', // Intended to be IP address, but we don't have access... BE will augment with value from header
        relatedEntityName: meta?.relatedEntityName,
        studyId: meta?.studyId,
        siteId: meta?.siteId,
        subjectId: meta?.subjectId,
        relatedEntityId: meta?.relatedEntityId,
        applicationSource: 'WEB',
        action,
        message,
        data,
    };
}

const LOG_STORAGE_KEY = 'ciAuditQueue';

function addLogToQueue(log: CIAuditLog): void {
    // The key may be missing: JSON.parse(null) returns null, which the checks below rely on (also applies to the reads further down)
    let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
    if (!logStorage) {
        logStorage = [];
    }

    const activeUser = localStorage.getItem('activeUser') as string;
    const userPk = (JSON.parse(activeUser) as AuthUser)?.pk;
    // Associate userId for each log who generated it so that can be send to server even after logout
    // TODO : support patient's logs as well in future for now userPk will result undefined
    logStorage.push({ id: uuid(), log: log, userPk });
    window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
}

const LOG_FLUSH_FREQ = 1000; // One second

export class CIAuditLogger {
    private static __instance: CIAuditLogger = new CIAuditLogger();

    private gqlClient: typeof loggerClient;
    private authUser: AuthState;
    private sendLogs;

    private constructor() {
        this.sendLogs = debounce((): void => {
            let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
            const activeUser = localStorage.getItem('activeUser') as string;
            const userPk = (JSON.parse(activeUser) as AuthUser)?.pk;

            if (!userPk || !logStorage || !logStorage.length) {
                window.localStorage.removeItem(LOG_STORAGE_KEY);

                return;
            }

            // let logs = map(logStorage, 'log');
            // let logKeys = map(logStorage, 'id');

            for (const logRecord of logStorage) {
                const result = this.gqlClient.mutate({
                    mutation: sendLogGQL,
                    variables: {
                        log: logRecord.log,
                    },
                });

                // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-loop-func
                result.then((result): void => {
                    logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
                    // eslint-disable-next-line lodash/prefer-immutable-method
                    remove(logStorage, { id: logRecord.id });

                    // parallel safe?
                    window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
                });
            }
        }, LOG_FLUSH_FREQ);

        this.initialize();
    }

    private initialize(): void {
        this.gqlClient = loggerClient;
        this.sendLogs();
    }

    public setAuth(auth: AuthState): void {
        this.authUser = auth;
    }

    public static getInstance(): CIAuditLogger {
        return CIAuditLogger.__instance;
    }

    public log(action: string | undefined, message: string, data: object | null, meta?: CIAuditLogMeta): void {
        this.writeLog(action, message, data, meta);
    }

    private writeLog(action: string | undefined, message: string, data: object | null, meta?: CIAuditLogMeta): void {
        const studyId =
            meta?.studyId || this.authUser?.activeStudySite?.activeStudy?.studyId || this.authUser?.activeUser?.studyId;
        const siteId = meta?.siteId || this.authUser?.activeStudySite?.activeSite?.siteId;

        if (!meta) {
            meta = {};
        }
        if (action !== AuditCreationEventsEnum.Logout && action !== AuditCreationEventsEnum.Login) {
            meta.studyId = studyId;
            meta.siteId = siteId;
        }
        const log = convertLogToAPI(action, message, data, meta);
        addLogToQueue(log);

        this.sendLogs();
    }
}

const AuditLoggingContext = createContext(CIAuditLogger.prototype);

export function AuditLoggerProvider(WrappedComponent: ComponentType): ComponentType {
    interface Props {
        auth: AuthState;
    }

    const _LoggerProvider: FC<Props> = (props): ReactElement => {
        const logger = CIAuditLogger.getInstance();
        logger.setAuth(props.auth);

        return (
            <AuditLoggingContext.Provider value={logger}>
                <WrappedComponent />
            </AuditLoggingContext.Provider>
        );
    };

    const LoggerProvider = memo(_LoggerProvider, (prevProps, nextProps): boolean => nextProps.auth === prevProps.auth);

    const mapStateToProps = ({ auth }: { auth: AuthState }): Props => ({ auth });

    return connect(mapStateToProps)(LoggerProvider);
}
// eslint-disable-next-line react-refresh/only-export-components
export function withAuditLogger<P extends object>(
    Component: ComponentType<P & { auditLogger: CIAuditLogger }>
): ComponentType<P> {
    return function WrapperComponent(props: P): ReactElement {
        return (
            <AuditLoggingContext.Consumer>
                {(logger): ReactElement => <Component {...props} auditLogger={logger} />}
            </AuditLoggingContext.Consumer>
        );
    };
}
